import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCase } from "../../context/CaseContext.tsx";
import { useCurrency } from "../../context/CurrencyContext.tsx";
import { useTheme } from "../../context/ThemeContext";
import { Quantum, getAwardedValue } from "../../shared/CaseModel";
import { percentage } from "../../shared/percentage";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { TonalBadge } from "../general/TonalBadge";
import { Party } from "./IssueModel";
import { useJudgment } from "./JudgedRow";
import { QuantumCell, QuantumProps } from "./QuantumCell";

export interface QuantumRowCallbacks {
  /**
   * @param quantum quantum to edit or undefined if a new quantum shall be created
   */
  onQuantumEditorRequested: (quantum?: Quantum) => void;
  onLiabilityUpdate: (
    quantum: Quantum,
    defendantId: string,
    newLiability: number,
  ) => void;
  onQuantumDelete: (quantum: Quantum) => void;
}

export const QuantumRow = ({
  claimId,
  issueTitle,
  issueOrdinal,
  quantum,
  name,
  defendants,
  callbacks,
  indent,
  summaryColumn = false,
}: {
  claimId: string;
  issueTitle: string;
  issueOrdinal: string;
  quantum: Quantum;
  // the name of the damage claimed
  name: string;
  defendants: Party[];
  callbacks: QuantumRowCallbacks;
  indent: boolean;
  summaryColumn?: boolean;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.claims.quantums",
  });
  const { readOnly } = useCase();
  const awarded = getAwardedValue(quantum);
  const judgment = useJudgment();
  const awardsOfDamage = judgment?.awards.filter(
    ({ damageId }) => damageId === quantum.damageId,
  );
  const award =
    awardsOfDamage?.find(({ issueId }) => issueId === claimId) ??
    awardsOfDamage?.[0];

  return (
    <TableRow>
      <TableCell sx={{ borderBottom: "none" }} colSpan={indent ? 1 : 2}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", ...TABULAR_NUMERALS }}
            >
              {t("amounts", {
                claimed: currency.format(quantum.value),
                awarded: currency.format(awarded),
              })}
            </Typography>
            {judgment && (
              <Typography
                variant="caption"
                component="p"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  ...TABULAR_NUMERALS,
                }}
              >
                {award
                  ? t("awardedInJudgment", {
                      amount: currency.format(award.amount),
                    })
                  : t("notAwarded")}
              </Typography>
            )}
          </Box>
          {/* each tooltip needs an element that fires events, which a
              disabled button does not */}
          <Tooltip title={t("tip.edit")}>
            <span>
              <IconButton
                size="small"
                aria-label={t("tip.edit")}
                disabled={readOnly}
                onClick={() => callbacks.onQuantumEditorRequested(quantum)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("tip.delete")}>
            <span>
              <IconButton
                size="small"
                aria-label={t("tip.delete")}
                disabled={readOnly}
                onClick={() => callbacks.onQuantumDelete(quantum)}
                sx={{ "&:hover": { color: theme.palette.data.negative.dark } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </TableCell>
      {defendants.map((defendant) => {
        const liability =
          (quantum.liabilityShares && quantum.liabilityShares[defendant.id]) ||
          0;
        const quantumCellProps: QuantumProps = {
          issueTitle,
          issueOrdinal,
          quantumTitle: name,
          quantumAmount: awarded,
        };
        return (
          <TableCell
            key={quantum.damageId + " " + defendant.id}
            sx={{ borderBottom: "none" }}
          >
            <QuantumCell
              quantum={quantumCellProps}
              defendant={defendant.name}
              onLiabilityUpdate={(value) =>
                callbacks.onLiabilityUpdate(quantum, defendant.id, value)
              }
              liability={liability}
            />
          </TableCell>
        );
      })}
      {summaryColumn && (
        <QuantumCoverage
          quantum={quantum}
          name={name}
          defendants={defendants}
          claimOrdinal={issueOrdinal}
        />
      )}
    </TableRow>
  );
};

/**
 * How much of a claimed amount the defendants' liabilities cover: at most, if
 * the claim is won against all of them, and at least, if it is won against the
 * one liable for the least.
 */
const QuantumCoverage = ({
  quantum,
  name,
  defendants,
  claimOrdinal,
}: {
  quantum: Quantum;
  name: string;
  defendants: Party[];
  claimOrdinal: string;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.claims.quantums.coverage",
  });

  // nothing claimed leaves nothing to cover
  if (quantum.value === 0)
    return (
      <>
        <TableCell align="right" sx={{ borderBottom: "none" }}>
          --
        </TableCell>
        <TableCell align="right" sx={{ borderBottom: "none" }}>
          --
        </TableCell>
      </>
    );

  let vAny, vAll;
  const insufficientlyLiableDefendants: string[] = [];
  if (defendants.length === 0) {
    vAny = 0;
    vAll = 0;
  } else {
    if (quantum.liabilityShares === undefined) {
      // all are equally liable
      vAny = 1;
      vAll = 1;
    } else {
      // must start with 1 as we are searching for minimum (starting with 0 would always remain at 0)
      vAny = 1;
      // v all is sum of liability shares of all defendants, thus start at 0
      vAll = 0;
      for (let i = 0; i < defendants.length; i++) {
        const defendant = defendants[i];
        const liability = quantum.liabilityShares[defendant.id] ?? 0;
        if (liability < 1) insufficientlyLiableDefendants.push(defendant.name);

        vAll = Math.min(vAll + liability, 1);
        vAny = Math.min(vAny, liability, 1);
      }
    }
  }

  const coverage = (covered: number, tooltip: string) => (
    <TableCell align="right" sx={{ borderBottom: "none" }}>
      <TonalBadge
        title={tooltip}
        tone={covered < 1 ? theme.palette.warning : theme.palette.data.positive}
        sx={{ typography: "body2", fontWeight: 700, ...TABULAR_NUMERALS }}
      >
        {percentage.format(covered)}
      </TonalBadge>
    </TableCell>
  );
  const values = { claimOrdinal, name };

  return (
    <>
      {coverage(vAll, t(vAll < 1 ? "all.partial" : "all.full", values))}
      {coverage(
        vAny,
        vAny < 1
          ? t("any.partial", {
              ...values,
              defendants: insufficientlyLiableDefendants.join(", "),
            })
          : t("any.full", values),
      )}
    </>
  );
};
